import React from 'react';
import I18n from '@/modules/i18n';
import cn from '@/utils/cn';
import type { ReadingSection } from './types';

interface SectionCheckboxListProps {
  disabled?: boolean;
  sections: ReadingSection[];
  selectedIDs: Set<number>;
  onDeselectAll: () => void;
  onSelectAll: () => void;
  onToggle: (sectionID: number) => void;
}

const SectionCheckboxList = ({
  disabled,
  sections,
  selectedIDs,
  onDeselectAll,
  onSelectAll,
  onToggle,
}: SectionCheckboxListProps) => {
  const allSelected = sections.length > 0 && selectedIDs.size === sections.length;

  const handleSelectAllChange = () => {
    if (allSelected) {
      onDeselectAll();
    } else {
      onSelectAll();
    }
  };

  return (
    <div className="tw-mb-2">
      {/* Label row — outside the bordered box */}
      <div className="tw-flex tw-items-center tw-justify-between tw-mb-1.5">
        <span
          className={cn(
            'tw-font-tci tw-text-sm tw-font-semibold tw-text-tci-black',
            'tw-leading-snug',
          )}
        >
          {I18n.t('ai_qb_reading_section')}
        </span>
        <span className="tw-font-tci tw-text-xs tw-text-dark-grey tw-leading-tight">
          {I18n.t('ai_qb_n_of_m_selected', { count: selectedIDs.size, total: sections.length })}
        </span>
      </div>
      {/* Bordered container with Select All + section checkboxes */}
      <div className="tw-border tw-border-solid tw-border-grey tw-rounded-md tw-overflow-hidden">
        {/* Select All header row */}
        <label
          className={cn(
            'tw-flex tw-items-center tw-gap-2.5 tw-px-3 tw-py-1.5',
            'tw-bg-light-grey tw-border-b tw-border-solid tw-border-grey',
            'tw-cursor-pointer tw-m-0',
            disabled && 'tw-opacity-50 tw-cursor-not-allowed',
          )}
        >
          <input
            checked={allSelected}
            className="tw-w-3.5 tw-h-3.5 tw-accent-[#1B63DC] tw-flex-none"
            disabled={disabled}
            type="checkbox"
            onChange={handleSelectAllChange}
          />
          <span className="tw-font-tci tw-text-xs tw-text-tci-black tw-leading-tight">
            {I18n.t('ai_qb_select_all')}
          </span>
        </label>
        {/* Section rows */}
        <div className="tw-h-[200px] tw-overflow-y-auto tw-bg-white tw-px-3 tw-py-2">
          {sections.map(section => (
            <label
              key={section.id}
              className={cn(
                'tw-flex tw-items-start tw-gap-2.5 tw-cursor-pointer',
                'tw-mt-0 tw-mx-0 tw-mb-1.5',
                disabled && 'tw-opacity-50 tw-cursor-not-allowed',
              )}
            >
              <input
                checked={selectedIDs.has(section.id)}
                className="tw-w-3.5 tw-h-3.5 tw-accent-[#1B63DC] tw-flex-none tw-mt-0.5"
                disabled={disabled}
                type="checkbox"
                onChange={() => onToggle(section.id)}
              />
              <span className="tw-font-tci tw-text-xs tw-text-tci-black tw-leading-tight">
                {section.title}
              </span>
            </label>
          ))}
        </div>
      </div>
    </div>
  );
};

export default SectionCheckboxList;
